import * as Dialog from '@radix-ui/react-dialog';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  type Client,
  Client as ClientSchema,
  isClientContractComplete,
} from '@uit/shared/schemas/client';
import { isLastCommaFirst } from '@uit/shared/name';
import { type VendorPickerResponse, VendorPickerResponse as VendorPickerResponseSchema } from '@uit/shared/schemas/vendor';
import { type ReactNode, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { z } from 'zod';
import { ApiError, apiMutation, useApi } from '../../lib/api.js';
import { useAuth } from '../../lib/auth.js';
import { useFeatureFlag } from '../../lib/feature-flags.js';
import { MagicLinkPanel } from './MagicLinkPanel.js';
import s from './NewOnboardingDialog.module.scss';

// Form-local schema: HTML inputs are strings, so rates are typed as strings
// and coerced before send. Client-side date format is YYYY-MM-DD.
const FormSchema = z.object({
  admin_resource_name: z
    .string()
    .min(1, 'Required')
    .max(200)
    .refine(isLastCommaFirst, 'Enter as Last, First (include a comma)'),
  admin_business_email: z.string().email('Invalid email'),
  admin_role: z.string().max(200),
  // '' = create a new vendor (the contractor fills business/banking/EIN);
  // a uuid = add this resource to that existing vendor (resource-only form).
  vendor_id: z.string(),
  client_id: z.string().uuid('Select a client to continue.'),
  rate_to_contractor: z
    .string()
    .min(1, 'Required')
    .refine((v) => Number(v) > 0, 'Must be a positive number'),
  rate_to_client: z
    .string()
    .refine((v) => v === '' || Number(v) > 0, 'Must be a positive number or blank'),
  start_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Required'),
  end_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Required'),
  product_service: z.string().min(1, 'Required').max(400),
  // Resource personal details (T-112) — optional, captured up front so the
  // RCF/laptop order can start at acceptance; pre-fill the vendor's form.
  personal_email: z.string().refine((v) => v === '' || z.string().email().safeParse(v).success, 'Invalid email'),
  resource_mobile_phone: z
    .string()
    .refine((v) => v === '' || /^\d{3}-\d{3}-\d{4}$/.test(v), 'Format: xxx-xxx-xxxx'),
  shipping_street: z.string().max(200),
  shipping_city: z.string().max(120),
  shipping_state: z.string().max(60),
  shipping_zip: z.string().max(20),
  // Mint the uIT O365 mailbox at create (T-140); default on.
  create_uit_mailbox: z.boolean(),
  // Referral capture (T-109). `referred` is UI-only (gates the dropdown);
  // `referred_by_vendor_id` ('' = none) is sent. If referred, a vendor is required.
  referred: z.boolean(),
  referred_by_vendor_id: z.string(),
}).refine((v) => !v.referred || v.referred_by_vendor_id !== '', {
  path: ['referred_by_vendor_id'],
  message: 'Pick the referring vendor',
});
type FormValues = z.infer<typeof FormSchema>;

const ClientList = z.array(ClientSchema);

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (onboarding: { id: string }) => void;
}

// Map a failed create to an actionable message. The server returns a typed
// `error` code (ApiError.code); each config/data gap gets its own message with a
// link to where it's actually fixed. The generic "check the fields" wording is
// reserved for real field validation (the zod 'validation' code) — landing any
// other cause there is what sent an admin field-hunting over a Settings gap.
function createErrorMessage(err: unknown, resourceName: string, clientId: string): ReactNode {
  const nameConflict = `A resource named "${resourceName}" already exists. Use a different name, or add this person to their existing vendor.`;
  const code = err instanceof ApiError ? err.code : null;
  switch (code) {
    case 'signer_email_not_configured':
      return (
        <>
          Set the uIT signer email in <Link to="/settings">Settings</Link> before creating an
          onboarding — it&apos;s the contract countersigner every onboarding needs.
        </>
      );
    case 'client_contract_fields_missing':
      return (
        <>
          This client&apos;s contract setup is incomplete.{' '}
          {clientId ? (
            <Link to={`/clients/${clientId}`}>Finish its contract fields</Link>
          ) : (
            'Finish its contract fields'
          )}{' '}
          before onboarding a resource under it.
        </>
      );
    case 'resource_name_already_exists':
      return nameConflict;
    case 'vendor_not_found':
      return 'The vendor you selected no longer exists. Refresh and try again.';
    case 'referring_vendor_not_found':
      return 'The referring vendor no longer exists. Refresh and pick another.';
    case 'client_not_found':
      return 'That client no longer exists. Refresh and try again.';
    case 'validation':
      return 'Please check the highlighted fields above.';
    default:
      break;
  }
  // Unmapped code: fall back to status, then a generic retry message.
  const status = err instanceof ApiError ? err.status : null;
  if (status === 409) return nameConflict;
  if (status === 404) return 'Something referenced here no longer exists. Refresh and try again.';
  return 'Could not create the onboarding. Please try again.';
}

export function NewOnboardingDialog({ open, onOpenChange, onCreated }: Props) {
  const { admin } = useAuth();
  // Feature gate (T-152): hide the mailbox toggle unless o365 provisioning is on.
  const o365Mailbox = useFeatureFlag('o365_mailbox');
  // Rate to client is owner-only (T-186) — with rate_to_contractor beside it,
  // the pair is the margin. It is optional at onboarding by design
  // (REQUIREMENTS.md), so a non-owner completes the form without it and the
  // owner fills it in later from the engagement. The server drops it too.
  const isOwner = !!admin?.is_owner;
  const { data: clients } = useApi<Client[]>(open ? '/api/clients' : null, ClientList);
  const { data: vendors } = useApi<VendorPickerResponse>(
    open ? '/api/vendors?page_size=100' : null,
    VendorPickerResponseSchema,
  );
  // Curated referrer list (T-127): the referred-by dropdown offers only vendors
  // flagged is_referrer in Settings — empty until one is flagged.
  const { data: referrerVendors } = useApi<VendorPickerResponse>(
    open ? '/api/vendors?referrer=true&page_size=100' : null,
    VendorPickerResponseSchema,
  );
  const [submitError, setSubmitError] = useState<ReactNode | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [createdLink, setCreatedLink] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, dirtyFields },
  } = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      admin_resource_name: '',
      admin_business_email: '',
      admin_role: '',
      vendor_id: '',
      client_id: '',
      rate_to_contractor: '',
      rate_to_client: '',
      start_date: '',
      end_date: '',
      product_service: '',
      personal_email: '',
      resource_mobile_phone: '',
      shipping_street: '',
      shipping_city: '',
      shipping_state: '',
      shipping_zip: '',
      create_uit_mailbox: true,
      referred: false,
      referred_by_vendor_id: '',
    },
  });

  const watchedName = watch('admin_resource_name');
  const watchedClientId = watch('client_id');
  const watchedReferred = watch('referred');

  // T-044 disables incomplete clients in the picker; T-050 surfaces *why* and a
  // path to fix it. `clients` is undefined until loaded — treat that as empty.
  const incompleteClients = (clients ?? []).filter((c) => !isClientContractComplete(c));
  const hasSelectableClient = (clients ?? []).some((c) => isClientContractComplete(c));

  // Service-name auto-derivation: only if the user hasn't manually edited the
  // product/service field. Stays editable.
  useEffect(() => {
    if (dirtyFields.product_service) return;
    const client = clients?.find((c) => c.id === watchedClientId);
    if (!client || !watchedName) return;
    setValue('product_service', `${client.project_or_product_name} | ${watchedName}`, {
      shouldDirty: false,
    });
  }, [watchedName, watchedClientId, clients, dirtyFields.product_service, setValue]);

  function close(next: boolean): void {
    onOpenChange(next);
    if (!next) {
      reset();
      setSubmitError(null);
      setCreatedLink(null);
    }
  }

  const onSubmit = async (values: FormValues): Promise<void> => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      // Only send personal-detail keys that were filled (T-112) — blanks are
      // omitted so they don't seed the contractor draft.
      const ifSet = (v: string): string | undefined => (v.trim() === '' ? undefined : v.trim());
      const payload = {
        admin_resource_name: values.admin_resource_name,
        admin_business_email: values.admin_business_email,
        admin_role: values.admin_role.trim() === '' ? null : values.admin_role,
        vendor_id: values.vendor_id === '' ? null : values.vendor_id,
        client_id: values.client_id,
        rate_to_contractor: Number(values.rate_to_contractor),
        rate_to_client:
          !isOwner || values.rate_to_client.trim() === '' ? null : Number(values.rate_to_client),
        start_date: values.start_date,
        end_date: values.end_date,
        product_service: values.product_service,
        admin_resource_email: ifSet(values.personal_email),
        admin_resource_mobile_phone: ifSet(values.resource_mobile_phone),
        admin_resource_mailing_address_street: ifSet(values.shipping_street),
        admin_resource_mailing_address_city: ifSet(values.shipping_city),
        admin_resource_mailing_address_state: ifSet(values.shipping_state),
        admin_resource_mailing_address_zip: ifSet(values.shipping_zip),
        create_uit_mailbox: values.create_uit_mailbox,
        referred_by_vendor_id:
          values.referred && values.referred_by_vendor_id !== ''
            ? values.referred_by_vendor_id
            : null,
      };
      const created = await apiMutation<typeof payload, { id: string; magic_link_url?: string }>(
        '/api/onboardings',
        payload,
      );
      onCreated(created);
      // No-email mode: keep the dialog open to show the copyable link.
      if (created.magic_link_url) {
        setCreatedLink(created.magic_link_url);
      } else {
        close(false);
      }
    } catch (err) {
      setSubmitError(createErrorMessage(err, watchedName, watchedClientId));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={close}>
      <Dialog.Portal>
        <Dialog.Overlay className={s.overlay} />
        <Dialog.Content className={s.content}>
          <Dialog.Title className={s.title}>
            {createdLink ? 'Onboarding created' : 'New onboarding'}
          </Dialog.Title>

          {createdLink ? (
            <div>
              <Dialog.Description className={s.description}>
                Send this one-time link to the vendor (e.g. paste into Teams). It opens their
                onboarding form.
              </Dialog.Description>
              <MagicLinkPanel url={createdLink} />
              <div className={s.actions}>
                <button type="button" className={s.submit} onClick={() => close(false)}>
                  Done
                </button>
              </div>
            </div>
          ) : (
            <>
              <Dialog.Description className={s.description}>
                uIT fills the admin-side fields here. The resource receives a magic link to complete
                their portion.
              </Dialog.Description>

              <form
                onSubmit={(e) => {
                  void handleSubmit(onSubmit)(e);
                }}
                noValidate
              >
                {submitError && (
                  <div className={s.formError} role="alert">
                    {submitError}
                  </div>
                )}

                <div className={s.field}>
                  <label className={s.label} htmlFor="ob-vendor">
                    Vendor (business)
                  </label>
                  <select id="ob-vendor" className={s.input} {...register('vendor_id')}>
                    <option value="">— New vendor (they fill business details) —</option>
                    {vendors?.items.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.business_name}
                        {v.business_address_city ? ` — ${v.business_address_city}` : ''}
                        {v.business_address_state ? `, ${v.business_address_state}` : ''}
                      </option>
                    ))}
                  </select>
                  <div className={s.hint}>
                    Pick an existing vendor to add another resource under it — the magic-link form
                    then collects only the resource&apos;s details.
                  </div>
                </div>

                <div className={s.field}>
                  <label className={s.label} htmlFor="ob-name">
                    Resource name (Last, First)
                  </label>
                  <input
                    id="ob-name"
                    type="text"
                    className={s.input}
                    placeholder="Kumar, Sanjeev"
                    aria-invalid={!!errors.admin_resource_name}
                    {...register('admin_resource_name')}
                  />
                  <div className={s.hint}>
                    Last, First — matches the billing file&apos;s Worker column and the service name.
                  </div>
                  {errors.admin_resource_name && (
                    <div className={s.fieldError}>{errors.admin_resource_name.message}</div>
                  )}
                </div>

                <div className={s.field}>
                  <label className={s.label} htmlFor="ob-email">
                    Business email
                  </label>
                  <input
                    id="ob-email"
                    type="email"
                    className={s.input}
                    aria-invalid={!!errors.admin_business_email}
                    {...register('admin_business_email')}
                  />
                  {errors.admin_business_email && (
                    <div className={s.fieldError}>{errors.admin_business_email.message}</div>
                  )}
                  <div className={s.hint}>Will receive the magic link + contract.</div>
                </div>

                <div className={s.field}>
                  <label className={s.label} htmlFor="ob-role">
                    Role (optional)
                  </label>
                  <input id="ob-role" type="text" className={s.input} {...register('admin_role')} />
                </div>

                <div className={s.field}>
                  <label className={s.label} htmlFor="ob-client">
                    Client
                  </label>
                  <select
                    id="ob-client"
                    className={s.input}
                    aria-invalid={!!errors.client_id}
                    {...register('client_id')}
                  >
                    <option value="">— Select —</option>
                    {clients?.map((c) => {
                      const complete = isClientContractComplete(c);
                      return (
                        <option key={c.id} value={c.id} disabled={!complete}>
                          {c.name} — {c.project_or_product_name}
                          {complete ? '' : ' — finish contract setup'}
                        </option>
                      );
                    })}
                  </select>
                  {errors.client_id && (
                    <div className={s.fieldError}>{errors.client_id.message}</div>
                  )}

                  {incompleteClients.length > 0 && (
                    <div
                      className={`${s.clientNotice} ${hasSelectableClient ? '' : s.clientNoticeEmpty}`}
                    >
                      <span className={s.clientNoticeTitle}>
                        {hasSelectableClient
                          ? 'Some clients need contract setup'
                          : 'No clients are ready for onboarding yet'}
                      </span>
                      {hasSelectableClient
                        ? 'These are greyed out until their contract fields are filled:'
                        : 'A client needs its contract fields filled before you can onboard a resource under it.'}
                      <ul className={s.completeList}>
                        {incompleteClients.map((c) => (
                          <li key={c.id}>
                            <span className={s.completeName}>
                              {c.name} — {c.project_or_product_name}
                            </span>
                            <Link
                              to={`/clients/${c.id}`}
                              className={s.completeLink}
                              onClick={() => close(false)}
                            >
                              Complete {c.name} →
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div className={s.row}>
                  <div className={s.field}>
                    <label className={s.label} htmlFor="ob-start">
                      Start date
                    </label>
                    <input
                      id="ob-start"
                      type="date"
                      className={s.input}
                      aria-invalid={!!errors.start_date}
                      {...register('start_date')}
                    />
                    {errors.start_date && (
                      <div className={s.fieldError}>{errors.start_date.message}</div>
                    )}
                  </div>
                  <div className={s.field}>
                    <label className={s.label} htmlFor="ob-end">
                      End date
                    </label>
                    <input
                      id="ob-end"
                      type="date"
                      className={s.input}
                      aria-invalid={!!errors.end_date}
                      {...register('end_date')}
                    />
                    {errors.end_date && (
                      <div className={s.fieldError}>{errors.end_date.message}</div>
                    )}
                  </div>
                </div>

                <div className={s.row}>
                  <div className={s.field}>
                    <label className={s.label} htmlFor="ob-rtc">
                      Rate to vendor (USD)
                    </label>
                    <input
                      id="ob-rtc"
                      type="number"
                      step="0.01"
                      className={s.input}
                      aria-invalid={!!errors.rate_to_contractor}
                      {...register('rate_to_contractor')}
                    />
                    {errors.rate_to_contractor && (
                      <div className={s.fieldError}>{errors.rate_to_contractor.message}</div>
                    )}
                  </div>
                  {isOwner && (
                    <div className={s.field}>
                      <label className={s.label} htmlFor="ob-rtcl">
                        Rate to client (USD, optional)
                      </label>
                      <input
                        id="ob-rtcl"
                        type="number"
                        step="0.01"
                        className={s.input}
                        aria-invalid={!!errors.rate_to_client}
                        {...register('rate_to_client')}
                      />
                      {errors.rate_to_client && (
                        <div className={s.fieldError}>{errors.rate_to_client.message}</div>
                      )}
                    </div>
                  )}
                </div>

                <div className={s.field}>
                  <label className={s.label} htmlFor="ob-ps">
                    Product / service
                  </label>
                  <input
                    id="ob-ps"
                    type="text"
                    className={s.input}
                    aria-invalid={!!errors.product_service}
                    {...register('product_service')}
                  />
                  {errors.product_service && (
                    <div className={s.fieldError}>{errors.product_service.message}</div>
                  )}
                  <div className={s.hint}>
                    Auto-derives from client + resource name; edit to override.
                  </div>
                </div>

                <div className={s.field}>
                  <label className={s.label} htmlFor="ob-personal-email">
                    Personal email (optional)
                  </label>
                  <input
                    id="ob-personal-email"
                    type="email"
                    className={s.input}
                    aria-invalid={!!errors.personal_email}
                    {...register('personal_email')}
                  />
                  {errors.personal_email && (
                    <div className={s.fieldError}>{errors.personal_email.message}</div>
                  )}
                  <div className={s.hint}>
                    The resource&apos;s own email + shipping address — capture here to start the
                    laptop order at acceptance. Both pre-fill the vendor&apos;s form, where they can
                    confirm or correct them.
                  </div>
                </div>

                <div className={s.field}>
                  <label className={s.label} htmlFor="ob-mobile">
                    Mobile phone (optional)
                  </label>
                  <input
                    id="ob-mobile"
                    type="tel"
                    className={s.input}
                    placeholder="123-456-7890"
                    aria-invalid={!!errors.resource_mobile_phone}
                    {...register('resource_mobile_phone')}
                  />
                  {errors.resource_mobile_phone && (
                    <div className={s.fieldError}>{errors.resource_mobile_phone.message}</div>
                  )}
                  <div className={s.hint}>Used on the RCF email; pre-fills the vendor&apos;s form.</div>
                </div>

                <div className={s.field}>
                  <label className={s.label} htmlFor="ob-ship-street">
                    Shipping address (optional)
                  </label>
                  <input
                    id="ob-ship-street"
                    type="text"
                    className={s.input}
                    placeholder="Street"
                    {...register('shipping_street')}
                  />
                </div>
                <div className={s.row}>
                  <div className={s.field}>
                    <label className={s.label} htmlFor="ob-ship-city">
                      City
                    </label>
                    <input
                      id="ob-ship-city"
                      type="text"
                      className={s.input}
                      {...register('shipping_city')}
                    />
                  </div>
                  <div className={s.field}>
                    <label className={s.label} htmlFor="ob-ship-state">
                      State
                    </label>
                    <input
                      id="ob-ship-state"
                      type="text"
                      className={s.input}
                      {...register('shipping_state')}
                    />
                  </div>
                  <div className={s.field}>
                    <label className={s.label} htmlFor="ob-ship-zip">
                      ZIP
                    </label>
                    <input
                      id="ob-ship-zip"
                      type="text"
                      className={s.input}
                      {...register('shipping_zip')}
                    />
                  </div>
                </div>

                {o365Mailbox && (
                <div className={s.referral}>
                  <label className={s.referralToggle}>
                    <input type="checkbox" {...register('create_uit_mailbox')} />
                    <span>
                      <span className={s.referralTitle}>Create a uIT email address (Office 365)</span>
                      <span className={s.referralSub}>
                        Mints FirstnameLastname@ultimumit.com now so it&apos;s ready for the RCF /
                        Cigna laptop order. Turn off for a resource that already has one or
                        doesn&apos;t need it.
                      </span>
                    </span>
                  </label>
                </div>
                )}

                <div className={s.referral}>
                  <label className={s.referralToggle}>
                    <input type="checkbox" {...register('referred')} />
                    <span>
                      <span className={s.referralTitle}>This engagement came from a referral</span>
                      <span className={s.referralSub}>
                        A vendor who sent us the resume and gets a per-hour referral fee — separate
                        from the business vendor above, and per engagement.
                      </span>
                    </span>
                  </label>

                  {watchedReferred && (
                    <div className={s.field} style={{ marginTop: 12, marginBottom: 0 }}>
                      <label className={s.label} htmlFor="ob-refby">
                        Referred by (vendor)
                      </label>
                      <select
                        id="ob-refby"
                        className={s.input}
                        aria-invalid={!!errors.referred_by_vendor_id}
                        {...register('referred_by_vendor_id')}
                      >
                        <option value="">— Select referring vendor —</option>
                        {referrerVendors?.items.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.business_name}
                            {v.business_address_city ? ` — ${v.business_address_city}` : ''}
                            {v.business_address_state ? `, ${v.business_address_state}` : ''}
                          </option>
                        ))}
                      </select>
                      <div className={s.hint}>
                        {referrerVendors && referrerVendors.items.length === 0
                          ? 'No vendors are flagged as referrers yet — manage the referrer list in Settings.'
                          : 'Referrer vendors only (managed in Settings). Drives referral reports.'}
                      </div>
                      {errors.referred_by_vendor_id && (
                        <div className={s.fieldError}>{errors.referred_by_vendor_id.message}</div>
                      )}
                    </div>
                  )}
                </div>

                <div className={s.actions}>
                  <button
                    type="button"
                    className={s.cancel}
                    onClick={() => {
                      close(false);
                    }}
                    disabled={submitting}
                  >
                    Cancel
                  </button>
                  <button type="submit" className={s.submit} disabled={submitting}>
                    {submitting ? 'Creating…' : 'Send invite'}
                  </button>
                </div>
              </form>
            </>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
